import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { loadXlsx } from '@extension/utils/lazy-lib';
import moment from 'moment';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectRecentCampaignLogsPerMessageId } from '@common-reducers/CampaignSelectors';
import { calculatedWaContactName } from '@common-reducers/WhatsAppSelectors';
import { isMessageOverdue } from '@/bt-common/services/utilsCommon';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type Props = {
    campaignId: string;
    campaignName?: string;
};

const getContactPhone = (contact: any): string => {
    const id: string | undefined = contact?.id;
    if (id?.includes('@c.us')) {
        return `+${id.replace('@c.us', '')}`;
    }
    if (id?.includes('@g.us')) {
        return id.replace('@g.us', '');
    }
    return '';
};

const sanitizeSheetName = (name: string): string => {
    // Excel sheet names are limited to 31 chars and may not contain : \ / ? * [ ]
    const cleaned = (name || 'Logs').replace(/[:\\/?*[\]]/g, ' ').trim();
    return cleaned.length > 31 ? cleaned.substring(0, 31) : (cleaned || 'Logs');
};

export default function ExportCampaignLogsButton({ campaignId, campaignName }: Props) {
    const { t } = useTranslation(namespace);

    // One log per contact — same data the "Sent" tab renders (already deduped to the
    // most-advanced status per contact).
    const logs = useAppSelector((state) => selectRecentCampaignLogsPerMessageId(state, campaignId));

    // Mirror the UI status badge labels (see MessageLogStatusBadge).
    const statusLabel = (log: any): string => {
        let status: string = log?.status ?? '';

        // "bt-sent" but past the retry limit is shown as "expired" in the UI.
        const embeddedMessage = log?.message;
        if (status === 'bt-sent' && embeddedMessage && isMessageOverdue(embeddedMessage)) {
            status = 'expired';
        }

        switch (status) {
            case 'bt-sent':        return t('campaignLogs.status.sending');
            case 'before-wa-send': return t('campaignLogs.status.beforeSend');
            case 'confirmed':      return t('campaignLogs.status.confirmed');
            case 'received':       return t('campaignLogs.status.received');
            case 'read':           return t('campaignLogs.status.read');
            case 'played':         return t('campaignLogs.status.interacted');
            case 'failed':         return t('campaignLogs.status.failed');
            case 'expired':        return t('campaignLogs.status.expired');
            case 'pending':        return t('campaignLogs.status.pending');
            case 'cancelled':      return t('campaignLogs.status.cancelled');
            default:               return status;
        }
    };

    const handleExport = async () => {
        if (!logs || logs.length === 0) return;

        // Lazily load the SheetJS core (content-script-safe WAR module) only on
        // export — keeps it out of the WhatsApp content-script startup path.
        const XLSX = await loadXlsx();

        const rows = logs.map((log: any) => {
            const contact = log?.message?.contactList?.[0];
            const messageText = typeof log?.message === 'string'
                ? log.message
                : log?.message?.message ?? '';
            const dateTime = log?.message?.createdAt
                ? moment(new Date(log.message.createdAt))
                : moment(log?.createdAt ? new Date(log.createdAt) : new Date());

            return {
                [t('campaignLogs.export.name')]: calculatedWaContactName(contact) ?? '',
                [t('campaignLogs.export.phone')]: getContactPhone(contact),
                [t('campaignLogs.export.message')]: messageText,
                [t('campaignLogs.export.status')]: statusLabel(log),
                [t('campaignLogs.export.date')]: dateTime.format('DD/MM/YYYY'),
                [t('campaignLogs.export.time')]: dateTime.format('HH:mm:ss'),
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(campaignName ?? t('campaignLogs.title')));

        const safeName = (campaignName ?? 'campaign').replace(/[^a-zA-Z0-9-_]+/g, '_');
        XLSX.writeFile(workbook, `${safeName}-logs.xlsx`);
    };

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!logs || logs.length === 0}
            className="gap-2"
        >
            <Download className="h-4 w-4" />
            <span>{t('campaignLogs.exportToExcel')}</span>
        </Button>
    );
}
